import { Check, Loader2, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  formatPrice,
  useOfferCampaignSellables,
  useTranslation,
  type OfferCampaignSellableOption,
  type TranslationKey,
} from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase as supabasePassthrough } from '@/lib/supabase';
import { cn } from '@/lib/utils';

export interface OfferCampaignSellablePickerProps {
  readonly storeId: string | null;
  readonly selectedVariantIds: readonly string[];
  readonly initialSelectedOptions?: readonly OfferCampaignSellableOption[];
  readonly disabled?: boolean;
  readonly errorKey?: TranslationKey | null;
  readonly onChange: (variantIds: readonly string[]) => void;
}

function mergeOptions(
  options: readonly OfferCampaignSellableOption[],
  initialOptions: readonly OfferCampaignSellableOption[],
): OfferCampaignSellableOption[] {
  const byVariantId = new Map<string, OfferCampaignSellableOption>();
  for (const option of initialOptions) byVariantId.set(option.variant_id, option);
  for (const option of options) byVariantId.set(option.variant_id, option);
  return [...byVariantId.values()];
}

function optionLabel(option: OfferCampaignSellableOption, defaultLabel: string): string {
  const summary = option.option_summary.trim();
  return summary || defaultLabel;
}

function optionContextLabel(
  option: OfferCampaignSellableOption,
  defaultLabel: string,
  translate: (key: TranslationKey, values?: Record<string, string>) => string,
): string {
  const variant = optionLabel(option, defaultLabel);
  if (!option.category_name) return variant;
  return translate('admin.offerCampaigns.picker.optionWithCategory' as TranslationKey, {
    category: option.category_name,
    variant,
  });
}

export function OfferCampaignSellablePicker({
  storeId,
  selectedVariantIds,
  initialSelectedOptions = [],
  disabled = false,
  errorKey,
  onChange,
}: OfferCampaignSellablePickerProps) {
  const { t, lang, dir } = useTranslation();
  const [search, setSearch] = useState('');
  const sellablesQuery = useOfferCampaignSellables(supabasePassthrough, {
    storeId,
    search,
  });

  const options = useMemo(
    () => mergeOptions(sellablesQuery.data ?? [], initialSelectedOptions),
    [initialSelectedOptions, sellablesQuery.data],
  );
  const selectedSet = useMemo(() => new Set(selectedVariantIds), [selectedVariantIds]);
  const selectedOptions = options.filter((option) => selectedSet.has(option.variant_id));

  function toggle(option: OfferCampaignSellableOption): void {
    if (disabled || !option.isSelectable) return;
    if (selectedSet.has(option.variant_id)) {
      onChange(selectedVariantIds.filter((id) => id !== option.variant_id));
      return;
    }
    onChange([...selectedVariantIds, option.variant_id]);
  }

  return (
    <section dir={dir} data-testid="offer-campaign-sellable-picker" className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 flex-1">
          <Label htmlFor="offer-campaign-sellable-search" className="mb-1.5 block text-start">
            {t('admin.offerCampaigns.picker.searchLabel' as TranslationKey)}
          </Label>
          <div className="relative">
            <Search
              className="text-muted-foreground pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              id="offer-campaign-sellable-search"
              type="search"
              className="ps-10"
              disabled={disabled || !storeId}
              value={search}
              placeholder={t('admin.offerCampaigns.picker.searchPlaceholder' as TranslationKey)}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
        <Badge variant="neutral">
          {t('admin.offerCampaigns.picker.selected' as TranslationKey, {
            count: selectedVariantIds.length,
          })}
        </Badge>
      </div>

      {errorKey ? (
        <p role="alert" className="text-destructive text-start text-xs">
          {t(errorKey)}
        </p>
      ) : null}

      {selectedOptions.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-testid="offer-campaign-selected-sellables">
          {selectedOptions.map((option) => (
            <button
              key={option.variant_id}
              type="button"
              disabled={disabled}
              className="border-border bg-admin-surface-muted text-foreground inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1 text-sm disabled:opacity-60"
              onClick={() => toggle(option)}
            >
              <span className="truncate">{option.product_name}</span>
              <span className="text-muted-foreground truncate text-xs">
                {optionLabel(
                  option,
                  t('admin.offerCampaigns.picker.defaultVariant' as TranslationKey),
                )}
              </span>
              <X className="size-3.5 shrink-0" aria-hidden />
            </button>
          ))}
        </div>
      ) : null}

      <div className="border-border overflow-hidden rounded-lg border">
        {sellablesQuery.isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2 p-4 text-start text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {t('admin.offerCampaigns.picker.loading' as TranslationKey)}
          </div>
        ) : null}

        {!sellablesQuery.isLoading && options.length === 0 ? (
          <p className="text-muted-foreground p-4 text-start text-sm">
            {t('admin.offerCampaigns.picker.empty' as TranslationKey)}
          </p>
        ) : null}

        {!sellablesQuery.isLoading && options.length > 0 ? (
          <ul className="divide-border max-h-96 divide-y overflow-y-auto">
            {options.map((option) => {
              const isSelected = selectedSet.has(option.variant_id);
              const canSelect = option.isSelectable && !disabled;
              return (
                <li key={option.variant_id}>
                  <button
                    type="button"
                    disabled={!canSelect && !isSelected}
                    className={cn(
                      'hover:bg-admin-surface-muted flex w-full items-start gap-3 p-4 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                      isSelected ? 'bg-admin-surface-muted' : 'bg-card',
                    )}
                    onClick={() => toggle(option)}
                  >
                    <span
                      className={cn(
                        'border-border mt-1 flex size-5 shrink-0 items-center justify-center rounded border',
                        isSelected ? 'bg-admin-lime text-primary-foreground' : 'bg-card',
                      )}
                    >
                      {isSelected ? <Check className="size-3.5" aria-hidden /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="text-foreground block truncate font-medium">
                        {option.product_name}
                      </span>
                      <span className="text-muted-foreground mt-1 block text-xs">
                        {optionContextLabel(
                          option,
                          t('admin.offerCampaigns.picker.defaultVariant' as TranslationKey),
                          t,
                        )}
                      </span>
                      <span className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-saffron text-sm font-semibold">
                          {formatPrice(option.sale_price_cents, lang)}
                        </span>
                        <span className="text-muted-foreground text-xs line-through">
                          {formatPrice(option.price_cents, lang)}
                        </span>
                        {!option.isSelectable ? (
                          <Badge variant="warning">
                            {t('admin.offerCampaigns.picker.ineligible' as TranslationKey)}
                          </Badge>
                        ) : null}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
